/**
 * PERFORMANCE MAX build path. Two objectives sit on it — Sales
 * (/google-ads/sales) and Local store visits (/google-ads/local). PMAX has no
 * ad groups or ads: a campaign holds ASSET GROUPS (text + images + videos
 * Google mixes into every format on Search, Display, YouTube, Gmail, Discover
 * and Maps). Local additionally links the account's Business Profile
 * locations so Google can optimise for store visits.
 *
 * Components are MODULE SCOPE on purpose: inline definitions remount on every
 * keystroke and lose focus.
 */
import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  Copy, Globe, Image as ImageIcon, Layers, Mail, Monitor, Play, Search, Shuffle, Target, Trash2, TrendingUp, Users, type LucideIcon,
} from 'lucide-react';
import { Input } from '@/components/Input';
import { Card } from '@/components/Card';
import { Select } from '@/components/Select';
import { MEDIA_FOLDERS } from '@/services/uploads.service';
import {
  AssetGroupCreatePayload, GoogleAdsAssetRef, GoogleAdsCampaignSummary, googleAdsService, GoogleAdsUserList, PmaxBidding, PmaxObjectiveKey,
} from '@/services/google-ads.service';
import { Label, Tag } from '@/components/google-ads/youtube/youtubeShared';
import {
  assetCache, createAssetCached, ensureImageAssets, hostOf, IMAGE_SPECS, ImageDraft, ImageGrid, isHttpUrl, Pub, StepIcon, uid,
} from '@/components/google-ads/objectives/objectiveShared';
import {
  badVideoLines, ChoiceOption, CountedInput, IconBtn, Panel, PreviewFrame, Segmented, strengthOf, StrengthMeter, StrengthPill, StrengthRow,
  TagInput, textIssues, TextLines, validVideoIds, YouTubeList,
} from '@/components/google-ads/objectives/wizardChrome';

// ── objective config ─────────────────────────────────────────────────────

export interface PmaxObjectiveConfig {
  key: PmaxObjectiveKey;
  title: string;
  base: string;
  subtitle: string;
  icon: LucideIcon;
  /** Local: link Business Profile locations (store goals). */
  storeGoals: boolean;
  /** Top-strip "Goal:" line. */
  goal: string;
  /** Default campaign name prefix. */
  namePrefix: string;
  emptyHint: string;
}

// ── bidding ──────────────────────────────────────────────────────────────

export const PMAX_BIDDING: ReadonlyArray<ChoiceOption<PmaxBidding>> = [
  { key: 'MAXIMIZE_CONVERSIONS', title: 'Maximize conversions', icon: Target,
    desc: 'The most purchases, leads or visits the budget can buy — the right start for most businesses.', hint: 'Optional target CPA' },
  { key: 'MAXIMIZE_CONVERSION_VALUE', title: 'Maximize conversion value', icon: TrendingUp,
    desc: 'Optimises for revenue instead of count. Needs a value on each conversion.', hint: 'Optional target ROAS' },
];
export const pmaxBiddingTitle = (b: PmaxBidding) => PMAX_BIDDING.find((o) => o.key === b)?.title ?? b;
export const isPmaxCampaign = (c: GoogleAdsCampaignSummary) => c.advertisingChannelType === 'PERFORMANCE_MAX';

export const PMAX_CTA_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Automated — Google picks per placement' },
  { value: 'LEARN_MORE', label: 'Learn more' }, { value: 'SHOP_NOW', label: 'Shop now' }, { value: 'BUY_NOW', label: 'Buy now' },
  { value: 'ORDER_NOW', label: 'Order now' }, { value: 'BOOK_NOW', label: 'Book now' }, { value: 'GET_QUOTE', label: 'Get quote' },
  { value: 'SIGN_UP', label: 'Sign up' }, { value: 'SUBSCRIBE', label: 'Subscribe' }, { value: 'CONTACT_US', label: 'Contact us' },
  { value: 'APPLY_NOW', label: 'Apply now' }, { value: 'DOWNLOAD', label: 'Download' }, { value: 'VISIT_SITE', label: 'Visit site' },
];

export const PMAX_LIMITS = {
  headlines: { min: 3, max: 15, chars: 30 },
  longHeadlines: { min: 1, max: 5, chars: 90 },
  descriptions: { min: 2, max: 5, chars: 90, firstChars: 60 },
  businessName: 25,
  images: 20,
  /** Square + landscape logos together. */
  logos: 5,
  videos: 15,
  searchThemes: 50,
  searchThemeWords: 10,
} as const;

// ── drafts ───────────────────────────────────────────────────────────────

export type AssetGroupDraft = {
  id: string; name: string; finalUrl: string; businessName: string; headlines: string[]; longHeadlines: string[]; descriptions: string[];
  cta: string; path1: string; path2: string;
  marketingImages: ImageDraft[]; squareImages: ImageDraft[]; portraitImages: ImageDraft[]; logos: ImageDraft[]; wideLogos: ImageDraft[];
  videos: string[];
  /** Audience signals. */
  searchThemes: string[]; audienceLists: string[];
};

/** Brand fields a new asset group inherits from its neighbour or the campaign's brand step. */
export type PmaxBrand = Pick<AssetGroupDraft, 'finalUrl' | 'businessName' | 'logos' | 'wideLogos'>;

export const newAssetGroup = (n: number, brand?: Partial<PmaxBrand>): AssetGroupDraft => ({
  id: uid(), name: `Asset group ${n}`, finalUrl: brand?.finalUrl ?? '', businessName: brand?.businessName ?? '',
  headlines: ['', '', '', '', ''], longHeadlines: [''], descriptions: ['', ''], cta: '', path1: '', path2: '',
  marketingImages: [], squareImages: [], portraitImages: [], logos: (brand?.logos ?? []).map((i) => ({ ...i })), wideLogos: (brand?.wideLogos ?? []).map((i) => ({ ...i })),
  videos: [''], searchThemes: [], audienceLists: [],
});
const cloneImages = (xs: ImageDraft[]) => xs.map((i) => ({ ...i }));
export const cloneAssetGroup = (g: AssetGroupDraft): AssetGroupDraft => ({
  ...g, id: uid(), name: g.name ? `${g.name} (copy)` : '', headlines: [...g.headlines], longHeadlines: [...g.longHeadlines], descriptions: [...g.descriptions],
  marketingImages: cloneImages(g.marketingImages), squareImages: cloneImages(g.squareImages), portraitImages: cloneImages(g.portraitImages),
  logos: cloneImages(g.logos), wideLogos: cloneImages(g.wideLogos), videos: [...g.videos], searchThemes: [...g.searchThemes], audienceLists: [...g.audienceLists],
});
export const brandOfGroup = (g: AssetGroupDraft | undefined): Partial<PmaxBrand> | undefined =>
  g && { finalUrl: g.finalUrl, businessName: g.businessName, logos: g.logos, wideLogos: g.wideLogos };

// ── validation + strength ────────────────────────────────────────────────

const filled = (xs: string[]) => xs.map((x) => x.trim()).filter(Boolean);
const ready = (xs: ImageDraft[]) => xs.filter((x) => x.url);
const distinct = (xs: string[]) => new Set(filled(xs).map((x) => x.toLowerCase())).size;
const pathOk = (p: string) => p.length <= 15 && !/[/?#\s]/.test(p);

export function assetGroupIssues(g: AssetGroupDraft): string[] {
  const i: string[] = [];
  if (!g.name.trim()) i.push('a name');
  if (!isHttpUrl(g.finalUrl)) i.push('a final URL (https://…)');
  if (!g.businessName.trim()) i.push('a business name');
  else if (g.businessName.length > PMAX_LIMITS.businessName) i.push(`a business name ≤ ${PMAX_LIMITS.businessName} characters`);
  i.push(...textIssues('headlines', g.headlines, PMAX_LIMITS.headlines.min, PMAX_LIMITS.headlines.chars));
  i.push(...textIssues('long headlines', g.longHeadlines, PMAX_LIMITS.longHeadlines.min, PMAX_LIMITS.longHeadlines.chars));
  i.push(...textIssues('descriptions', g.descriptions, PMAX_LIMITS.descriptions.min, PMAX_LIMITS.descriptions.chars, PMAX_LIMITS.descriptions.firstChars));
  if (ready(g.marketingImages).length < 1) i.push('a landscape image');
  if (ready(g.squareImages).length < 1) i.push('a square image');
  if (ready(g.logos).length < 1) i.push('a square logo');
  if (g.logos.length + g.wideLogos.length > PMAX_LIMITS.logos) i.push(`at most ${PMAX_LIMITS.logos} logos in total`);
  const longThemes = g.searchThemes.filter((t) => t.trim().split(/\s+/).length > PMAX_LIMITS.searchThemeWords).length;
  if (longThemes) i.push(`search themes of ${PMAX_LIMITS.searchThemeWords} words or fewer`);
  if ([...g.marketingImages, ...g.squareImages, ...g.portraitImages, ...g.logos, ...g.wideLogos].some((x) => !x.url)) i.push('uploads to finish');
  if (!pathOk(g.path1) || !pathOk(g.path2)) i.push('display paths ≤ 15 characters, no spaces or slashes');
  if (g.path2 && !g.path1) i.push('path 1 before path 2');
  const bad = badVideoLines(g.videos);
  if (bad) i.push(`${bad} video link${bad === 1 ? '' : 's'} fixed (not a YouTube URL)`);
  return i;
}

/**
 * Google's guidance for an "Excellent" Performance Max asset group: 15
 * headlines (one of them ≤15 characters), 5 long headlines, 5 descriptions,
 * at least 4 landscape, 4 square and 2 portrait images, a logo, and your own
 * video in each orientation (otherwise Google auto-builds one).
 */
export function assetGroupStrengthRows(g: AssetGroupDraft | null): StrengthRow[] {
  if (!g) return [];
  return [
    { label: 'Headlines', have: distinct(g.headlines), min: 3, target: 15, weight: 2 },
    { label: 'Long headlines', have: distinct(g.longHeadlines), min: 1, target: 5, weight: 1.5 },
    { label: 'Descriptions', have: distinct(g.descriptions), min: 2, target: 5, weight: 1.5 },
    { label: 'Landscape images', have: ready(g.marketingImages).length, min: 1, target: 4, weight: 1.5 },
    { label: 'Square images', have: ready(g.squareImages).length, min: 1, target: 4, weight: 1.5 },
    { label: 'Portrait images', have: ready(g.portraitImages).length, min: 0, target: 2, weight: 1 },
    { label: 'Logos', have: ready(g.logos).length + ready(g.wideLogos).length, min: 1, target: 1, weight: 1 },
    { label: 'Videos', have: validVideoIds(g.videos).length, min: 0, target: 3, weight: 1 },
  ];
}
export const assetGroupStrength = (g: AssetGroupDraft | null) => {
  const s = strengthOf(assetGroupStrengthRows(g));
  // Google's own tip: at least one headline of 15 characters or fewer, for the smallest placements.
  if (g && s.level > 0 && !filled(g.headlines).some((h) => h.length <= 15)) {
    return { ...s, tips: [...s.tips, 'Add one short headline (15 characters or fewer) for the smallest placements.'].slice(0, 3) };
  }
  return s;
};

export const assetGroupImageCount = (g: AssetGroupDraft) => ready(g.marketingImages).length + ready(g.squareImages).length + ready(g.portraitImages).length;
export const assetGroupSummary = (g: AssetGroupDraft) => {
  const imgs = assetGroupImageCount(g);
  const logos = ready(g.logos).length + ready(g.wideLogos).length;
  const vids = validVideoIds(g.videos).length;
  return [
    `${filled(g.headlines).length} headlines`, `${filled(g.descriptions).length} descriptions`,
    `${imgs} image${imgs === 1 ? '' : 's'}`, `${logos} logo${logos === 1 ? '' : 's'}`,
    vids ? `${vids} video${vids === 1 ? '' : 's'}` : null,
    g.searchThemes.length || g.audienceLists.length ? 'signals' : null,
  ].filter(Boolean).join(' · ');
};

// ── editor ───────────────────────────────────────────────────────────────

export function AssetGroupEditor({ group, onChange, disabled, audiences }: {
  group: AssetGroupDraft; onChange: (p: Partial<AssetGroupDraft> | ((cur: AssetGroupDraft) => Partial<AssetGroupDraft>)) => void; disabled?: boolean;
  /** Customer Match lists on the account (for the audience signal); null while loading. */
  audiences: GoogleAdsUserList[] | null;
}) {
  const images = (field: 'marketingImages' | 'squareImages' | 'portraitImages' | 'logos' | 'wideLogos') =>
    (f: (cur: ImageDraft[]) => ImageDraft[]) => onChange((cur) => ({ [field]: f(cur[field]) }));
  const host = isHttpUrl(group.finalUrl) ? hostOf(group.finalUrl) : 'yourbusiness.com';
  const toggleList = (rn: string) => onChange((cur) => ({ audienceLists: cur.audienceLists.includes(rn) ? cur.audienceLists.filter((x) => x !== rn) : [...cur.audienceLists, rn] }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label>Asset group name</Label>
          <Input className="mt-1" placeholder="e.g. Summer collection" value={group.name} disabled={disabled} onChange={(e) => onChange({ name: e.target.value })} />
        </div>
        <div>
          <Label>Final URL</Label>
          <Input className="mt-1" iconLeft={<Globe size={13} />} placeholder="https://yourbusiness.com/collection" value={group.finalUrl} disabled={disabled}
            onChange={(e) => onChange({ finalUrl: e.target.value.trim() })} />
        </div>
        <div>
          <Label>Business name</Label>
          <CountedInput className="mt-1" value={group.businessName} maxLen={PMAX_LIMITS.businessName} placeholder="Your brand" disabled={disabled}
            onChange={(v) => onChange({ businessName: v })} ariaLabel="Business name" />
        </div>
        <div>
          <Label>Call to action</Label>
          <Select className="mt-1 h-10 rounded-lg" value={group.cta} disabled={disabled} onChange={(e) => onChange({ cta: e.target.value })}>
            {PMAX_CTA_OPTIONS.map((c) => <option key={c.value || 'auto'} value={c.value}>{c.label}</option>)}
          </Select>
        </div>
      </div>
      <div>
        <Label>Display URL</Label>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
          <span className="max-w-[40%] truncate rounded-lg bg-surface-alt px-2.5 py-2 font-medium text-text-secondary">{host}</span>
          <span>/</span>
          <Input className="!w-36" maxLength={15} placeholder="path-1" value={group.path1} disabled={disabled} aria-label="Display path 1"
            onChange={(e) => onChange({ path1: e.target.value.replace(/[/?#\s]/g, '') })} />
          <span>/</span>
          <Input className="!w-36" maxLength={15} placeholder="path-2" value={group.path2} disabled={disabled} aria-label="Display path 2"
            onChange={(e) => onChange({ path2: e.target.value.replace(/[/?#\s]/g, '') })} />
        </div>
        <p className="mt-1 text-2xs text-text-muted">Optional words shown after your domain on Search ads (≤ 15 characters each).</p>
      </div>

      <Panel title="Text" hint="Google combines these across Search, Display, YouTube, Gmail, Discover and Maps — more variety means more combinations it can test.">
        <div className="space-y-4">
          <TextLines label="Headlines" items={group.headlines} onChange={(v) => onChange({ headlines: v })} min={PMAX_LIMITS.headlines.min}
            max={PMAX_LIMITS.headlines.max} maxLen={PMAX_LIMITS.headlines.chars} disabled={disabled} cols={2}
            hint="3–15, each ≤ 30 characters. Mix offers, products, benefits and your brand — aim for 11 or more."
            placeholder={['Summer Collection 2026', 'Free Delivery in the UAE', 'Shop the New Arrivals', 'Up to 40% Off This Week', 'Handmade in Dubai']} />
          <TextLines label="Long headlines" items={group.longHeadlines} onChange={(v) => onChange({ longHeadlines: v })} min={PMAX_LIMITS.longHeadlines.min}
            max={PMAX_LIMITS.longHeadlines.max} maxLen={PMAX_LIMITS.longHeadlines.chars} disabled={disabled}
            placeholder={['Discover the pieces everyone in Dubai is wearing this season']} />
          <TextLines label="Descriptions" items={group.descriptions} onChange={(v) => onChange({ descriptions: v })} min={PMAX_LIMITS.descriptions.min}
            max={PMAX_LIMITS.descriptions.max} maxLen={PMAX_LIMITS.descriptions.chars} firstMaxLen={PMAX_LIMITS.descriptions.firstChars} disabled={disabled}
            hint="The first must be 60 characters or fewer — Google's short description."
            placeholder={['New drops every week. Free returns.', 'Order before 6pm for same-day delivery across the UAE.']} />
        </div>
      </Panel>

      <Panel title="Images & logos" hint="At least one landscape image, one square image and one square logo. Portrait images unlock vertical placements (YouTube Shorts, Discover).">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ImageGrid label="Landscape images" spec={IMAGE_SPECS.landscape} images={group.marketingImages} min={1} max={PMAX_LIMITS.images}
            folder={MEDIA_FOLDERS.googlePmax} disabled={disabled} onChange={images('marketingImages')} />
          <ImageGrid label="Square images" spec={IMAGE_SPECS.square} images={group.squareImages} min={1} max={PMAX_LIMITS.images}
            folder={MEDIA_FOLDERS.googlePmax} disabled={disabled} onChange={images('squareImages')} />
          <ImageGrid label="Portrait images" spec={IMAGE_SPECS.portrait} images={group.portraitImages} max={PMAX_LIMITS.images} optional
            folder={MEDIA_FOLDERS.googlePmax} disabled={disabled} onChange={images('portraitImages')} />
          <ImageGrid label="Square logo" spec={IMAGE_SPECS.logo} images={group.logos} min={1} max={Math.max(1, PMAX_LIMITS.logos - group.wideLogos.length)}
            folder={MEDIA_FOLDERS.googlePmax} disabled={disabled} onChange={images('logos')} />
          <ImageGrid label="Landscape logo" spec={IMAGE_SPECS.logoWide} images={group.wideLogos} max={Math.max(0, PMAX_LIMITS.logos - group.logos.length)} optional
            folder={MEDIA_FOLDERS.googlePmax} disabled={disabled} onChange={images('wideLogos')} />
        </div>
      </Panel>

      <Panel title="Videos" hint="Optional but recommended — up to 15, each at least 10 seconds. Without one, Google auto-generates a video from your images. Add horizontal, vertical and square versions for every YouTube placement.">
        <YouTubeList items={group.videos} onChange={(v) => onChange({ videos: v })} max={PMAX_LIMITS.videos} disabled={disabled} />
      </Panel>

      <Panel title="Audience signals" hint="Optional hints that speed up Google's learning. Performance Max still reaches beyond them when it finds converting customers.">
        <div className="space-y-3">
          <div>
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-2xs font-semibold text-text-secondary">Search themes</span>
              <span className="text-2xs tabular-nums text-text-muted">{group.searchThemes.length}/{PMAX_LIMITS.searchThemes}</span>
            </div>
            <p className="mb-1.5 text-2xs text-text-muted">Words and phrases your customers search for. Enter or paste a list.</p>
            <TagInput values={group.searchThemes} onChange={(v) => onChange({ searchThemes: v })} max={PMAX_LIMITS.searchThemes} disabled={disabled}
              placeholder="e.g. abaya online, modest fashion dubai"
              problem={(v) => (v.trim().split(/\s+/).length > PMAX_LIMITS.searchThemeWords ? `Over ${PMAX_LIMITS.searchThemeWords} words` : v.length > 80 ? 'Over 80 characters' : null)} />
          </div>
          <div>
            <span className="text-2xs font-semibold text-text-secondary">Your customer lists</span>
            <p className="mb-1.5 text-2xs text-text-muted">CRM contact lists you synced to Google (Measurement → Audiences). Google looks for people like them.</p>
            {audiences === null ? <div className="skeleton h-9 rounded-lg" /> : audiences.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border px-3 py-2.5 text-2xs text-text-muted">
                No customer lists on this account yet — sync a contact group in Measurement → Audiences to use it as a signal.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {audiences.map((a) => {
                  const on = group.audienceLists.includes(a.resourceName);
                  return (
                    <button key={a.resourceName} type="button" disabled={disabled} onClick={() => toggleList(a.resourceName)} aria-pressed={on}
                      className={clsx('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-2xs font-medium transition-colors disabled:opacity-60',
                        on ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-secondary hover:border-primary/40')}>
                      <Users size={11} />{a.name}
                      {a.sizeForDisplay ? <span className="text-text-muted">· {Number(a.sizeForDisplay).toLocaleString()}</span> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Panel>
    </div>
  );
}

/** Asset-group draft card with its editor — shared by the campaign workspace and the "add asset groups" page. */
export function AssetGroupCard({ group: g, index, open, onToggle, state, made, busy, onChange, onDuplicate, onRemove, onFocus, focused, audiences }: {
  group: AssetGroupDraft; index: number; open: boolean; onToggle: () => void; state?: Pub; made: boolean; busy: boolean;
  onChange: (p: Partial<AssetGroupDraft> | ((cur: AssetGroupDraft) => Partial<AssetGroupDraft>)) => void;
  onDuplicate?: () => void; onRemove?: () => void; onFocus: () => void; focused: boolean; audiences: GoogleAdsUserList[] | null;
}) {
  const issues = assetGroupIssues(g);
  const thumb = g.marketingImages.find((i) => i.url)?.url ?? g.squareImages.find((i) => i.url)?.url;
  return (
    <Card padded={false} className={clsx('overflow-hidden transition-colors', focused && open && 'border-primary/40')}>
      <div className={clsx('flex items-center gap-2.5 px-4 py-3', open && 'border-b border-border')} onFocusCapture={onFocus}>
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2.5 text-left" onClick={() => { onToggle(); onFocus(); }} aria-expanded={open}>
          {state && state !== 'idle' ? <StepIcon s={state} />
            : thumb ? <img src={thumb} alt="" className="h-7 w-12 shrink-0 rounded object-cover" />
            : <Layers size={15} className="shrink-0 text-text-muted" />}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-text-primary">{g.name.trim() || `Asset group ${index + 1}`}</div>
            <div className="truncate text-2xs text-text-muted">{assetGroupSummary(g)}</div>
          </div>
        </button>
        <StrengthPill s={assetGroupStrength(g)} className="hidden sm:inline-flex" />
        {made ? <Tag tone="ok">created</Tag> : issues.length ? <Tag tone="warn">{issues.length} missing</Tag> : <Tag tone="ok">ready</Tag>}
        {onDuplicate && <IconBtn title="Duplicate asset group" onClick={onDuplicate} disabled={busy}><Copy size={14} /></IconBtn>}
        {onRemove && !made && <IconBtn danger title="Remove asset group" onClick={onRemove} disabled={busy}><Trash2 size={14} /></IconBtn>}
      </div>
      {open && (
        <div className="p-4" onFocusCapture={onFocus}>
          <AssetGroupEditor group={g} onChange={onChange} disabled={made || busy} audiences={audiences} />
        </div>
      )}
    </Card>
  );
}

// ── preview ──────────────────────────────────────────────────────────────

type Surface = 'search' | 'display' | 'youtube' | 'gmail';
const SURFACES: ReadonlyArray<{ value: Surface; label: string; icon: LucideIcon }> = [
  { value: 'search', label: 'Search', icon: Search },
  { value: 'display', label: 'Display', icon: Monitor },
  { value: 'youtube', label: 'YouTube', icon: Play },
  { value: 'gmail', label: 'Gmail', icon: Mail },
];
const rotate = <T,>(xs: T[], by: number) => (xs.length ? [...xs.slice(by % xs.length), ...xs.slice(0, by % xs.length)] : xs);

const Mark = ({ logo, brand, size = 'h-8 w-8' }: { logo?: string; brand: string; size?: string }) => (
  <span className={clsx('flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#e8f0fe] text-[11px] font-bold uppercase text-[#1a73e8]', size)}>
    {logo ? <img src={logo} alt="" className="h-full w-full bg-white object-contain" /> : brand.charAt(0)}
  </span>
);
const Picture = ({ src, className }: { src?: string; className: string }) => (
  <div className={clsx('overflow-hidden bg-[#f1f3f4] dark:bg-[#2a2a2a]', className)}>
    {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-[#9aa0a6]"><ImageIcon size={16} /></div>}
  </div>
);

/** Four of the surfaces one asset group serves on, driven by the group being edited. */
export function AssetGroupPreview({ group }: { group: AssetGroupDraft | null }) {
  const [surface, setSurface] = useState<Surface>('search');
  const [shift, setShift] = useState(0);
  const rows = useMemo(() => assetGroupStrengthRows(group), [group]);
  const strength = useMemo(() => strengthOf(rows), [rows]);
  const hs = rotate(filled(group?.headlines ?? []), shift);
  const lhs = rotate(filled(group?.longHeadlines ?? []), shift);
  const ds = rotate(filled(group?.descriptions ?? []), shift);
  const landscape = rotate(ready(group?.marketingImages ?? []).map((i) => i.url), shift);
  const square = rotate(ready(group?.squareImages ?? []).map((i) => i.url), shift);
  const logo = ready(group?.logos ?? [])[0]?.url;
  const host = group?.finalUrl && isHttpUrl(group.finalUrl) ? hostOf(group.finalUrl) : 'yourbusiness.com';
  const brand = group?.businessName.trim() || host;
  const cta = PMAX_CTA_OPTIONS.find((c) => c.value === group?.cta && c.value)?.label ?? 'Learn more';
  const path = [group?.path1, group?.path2].filter(Boolean).join(' › ');
  const videoId = validVideoIds(group?.videos ?? [])[0];
  const shell = 'overflow-hidden rounded-xl border border-border bg-white text-[#202124] shadow-soft dark:bg-[#1f1f1f] dark:text-[#e8eaed]';
  const muted = 'text-[#5f6368] dark:text-[#9aa0a6]';

  return (
    <PreviewFrame
      title="Preview · across Google"
      controls={(
        <button type="button" onClick={() => setShift((n) => n + 1)} title="Show another combination" aria-label="Show another combination"
          className="flex h-6 w-6 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary"><Shuffle size={13} /></button>
      )}
      footer={group ? <StrengthMeter s={strength} rows={rows} title="Asset strength" /> : undefined}
      note="Indicative only — Performance Max mixes these assets per surface, device and person. Google's own asset strength appears after review.">
      {!group ? <p className="py-10 text-center text-xs text-text-muted">Add an asset group to see it previewed here.</p> : (
        <>
          <div className="mb-3 flex justify-center"><Segmented options={SURFACES} value={surface} onChange={setSurface} size="xs" ariaLabel="Surface" /></div>

          {surface === 'search' && (
            <div className={clsx(shell, 'p-3.5')}>
              <div className="text-[11px] font-bold">Sponsored</div>
              <div className="mt-1.5 flex items-center gap-2">
                <Mark logo={logo} brand={brand} size="h-7 w-7" />
                <div className="min-w-0 leading-tight">
                  <div className="truncate text-xs font-medium">{brand}</div>
                  <div className={clsx('truncate text-[11px]', muted)}>https://{host}{path ? ` › ${path}` : ''}</div>
                </div>
              </div>
              <div className="mt-1.5 text-[16px] leading-snug text-[#1a0dab] dark:text-[#8ab4f8]">
                {hs.slice(0, 3).join(' | ') || <span className={muted}>Your headlines appear here</span>}
              </div>
              <div className={clsx('mt-1 text-[12px] leading-snug', muted)}>{ds.slice(0, 2).join(' ') || 'Your descriptions appear here.'}</div>
            </div>
          )}

          {surface === 'display' && (
            <div className={shell}>
              <Picture src={landscape[0] ?? square[0]} className="aspect-[1.91/1] w-full" />
              <div className="flex items-start gap-3 p-3">
                <Mark logo={logo} brand={brand} />
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 text-[14px] font-semibold leading-snug">{lhs[0] || hs[0] || 'Your long headline'}</div>
                  <div className={clsx('mt-0.5 line-clamp-2 text-[12px] leading-snug', muted)}>{ds[0] || 'Your description appears here.'}</div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className={clsx('truncate text-[11px]', muted)}>Ad · {brand}</span>
                    <span className="shrink-0 rounded-full bg-[#1a73e8] px-3 py-1 text-[11px] font-semibold text-white">{cta}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {surface === 'youtube' && (
            <div className={shell}>
              <div className="relative aspect-video w-full bg-black">
                {videoId
                  ? <img src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`} alt="" className="h-full w-full object-cover" />
                  : <Picture src={landscape[0] ?? square[0]} className="h-full w-full" />}
                <span className="absolute inset-0 flex items-center justify-center"><Play size={30} className="fill-white text-white drop-shadow" /></span>
                <span className="absolute bottom-2 left-2 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-medium text-white">Ad · {videoId ? 'your video' : 'auto-built from images'}</span>
              </div>
              <div className="flex gap-2.5 p-3">
                <Mark logo={logo} brand={brand} size="h-9 w-9" />
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 text-[13px] font-medium leading-snug">{hs[0] || 'Your headline appears here'}</div>
                  <div className={clsx('mt-0.5 truncate text-[11px]', muted)}>{brand} · Sponsored</div>
                </div>
              </div>
              <div className="border-t border-[#dadce0] p-2.5 dark:border-[#3c4043]">
                <div className="rounded-full bg-[#1a73e8] px-3 py-1.5 text-center text-[12px] font-semibold text-white">{cta}</div>
              </div>
            </div>
          )}

          {surface === 'gmail' && (
            <div className={clsx(shell, 'divide-y divide-[#eceff1] dark:divide-[#2f3033]')}>
              <div className="flex items-start gap-2.5 bg-[#f8fbff] p-3 dark:bg-[#1c2530]">
                <Mark logo={logo} brand={brand} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="rounded border border-[#1a73e8]/40 px-1 text-[9px] font-bold text-[#1a73e8]">Ad</span>
                    <span className="truncate text-[12px] font-semibold">{brand}</span>
                  </div>
                  <div className="truncate text-[12px] font-medium">{hs[0] || 'Your headline'}</div>
                  <div className={clsx('truncate text-[11px]', muted)}>{ds[0] || 'Your description appears here.'}</div>
                </div>
                {square[0] && <Picture src={square[0]} className="h-11 w-11 shrink-0 rounded-md" />}
              </div>
              {['Your order has shipped', 'Weekly team update', 'Invoice for September'].map((t) => (
                <div key={t} className="flex items-center gap-2.5 px-3 py-2.5">
                  <span className="h-6 w-6 shrink-0 rounded-full bg-[#e8eaed] dark:bg-[#3c4043]" />
                  <div className="min-w-0 flex-1">
                    <div className="h-1.5 w-1/3 rounded bg-[#dadce0] dark:bg-[#3c4043]" />
                    <div className={clsx('mt-1 truncate text-[11px]', muted)}>{t}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </PreviewFrame>
  );
}

// ── publish ──────────────────────────────────────────────────────────────

type ImgField = 'marketingImages' | 'squareImages' | 'portraitImages' | 'logos' | 'wideLogos';

/** Uploads → Google assets (cached) → asset group in one atomic mutate, then its audience signals. */
export async function publishAssetGroup(
  accountUid: string, campaignId: string, g: AssetGroupDraft,
  writeAsset: (field: ImgField, id: string, resourceName: string) => void,
): Promise<{ resourceName: string; assetGroupId: string; signals?: { added: number; error?: string } }> {
  const create = createAssetCached(accountUid, (b) => googleAdsService.performanceMax.createAsset(accountUid, b) as Promise<GoogleAdsAssetRef>);
  const marketing = await ensureImageAssets(g.marketingImages, create, (id, rn) => writeAsset('marketingImages', id, rn));
  const square = await ensureImageAssets(g.squareImages, create, (id, rn) => writeAsset('squareImages', id, rn));
  const portrait = await ensureImageAssets(g.portraitImages, create, (id, rn) => writeAsset('portraitImages', id, rn));
  const logos = await ensureImageAssets(g.logos, create, (id, rn) => writeAsset('logos', id, rn));
  const wideLogos = await ensureImageAssets(g.wideLogos, create, (id, rn) => writeAsset('wideLogos', id, rn));
  const videos: string[] = [];
  for (const v of validVideoIds(g.videos)) {
    const key = `${accountUid}:yt:${v}`;
    let rn = assetCache.get(key);
    if (!rn) { rn = (await googleAdsService.performanceMax.createAsset(accountUid, { youtubeVideoId: v })).resourceName; assetCache.set(key, rn); }
    videos.push(rn);
  }
  const body: AssetGroupCreatePayload = {
    campaignId, name: g.name.trim(), finalUrls: [g.finalUrl.trim()], status: 'PAUSED', businessName: g.businessName.trim(),
    headlines: filled(g.headlines), longHeadlines: filled(g.longHeadlines), descriptions: filled(g.descriptions),
    marketingImages: marketing, squareMarketingImages: square, logoImages: logos,
    landscapeLogoImages: wideLogos.length ? wideLogos : undefined,
    portraitMarketingImages: portrait.length ? portrait : undefined,
    youtubeVideos: videos.length ? videos : undefined,
    callToAction: g.cta || undefined, path1: g.path1 || undefined, path2: g.path2 || undefined,
    searchThemes: g.searchThemes.length ? g.searchThemes : undefined,
    audienceUserLists: g.audienceLists.length ? g.audienceLists : undefined,
  };
  return googleAdsService.performanceMax.createAssetGroup(accountUid, body);
}

/** Immutable write of an asset resource name into one of a group's image lists. */
export const writeAssetInto = (field: ImgField, id: string, rn: string) => (cur: AssetGroupDraft): Partial<AssetGroupDraft> => ({
  [field]: cur[field].map((im) => (im.id === id ? { ...im, resourceName: rn } : im)),
});

export const Muted = ({ children }: { children: React.ReactNode }) => <span className="text-text-muted">{children}</span>;
